/**
 * How far each workshop has got with its own setup.
 *
 * A signup that stalled before its first invoice is the one worth a phone
 * call, so those sort to the top and say so.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Mail, PhoneCall, Route } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';

export const OnboardingProgress = () => {
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [due, setDue] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/platform/onboarding`, { headers: authHeader() });
      setData(d);
    } catch { setData(null); } finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  // Dry run by default: see who would get what before anything leaves.
  const preview = async () => {
    setPreviewing(true);
    try {
      const { data: d } = await axios.post(`${API}/platform/onboarding/welcome-now`, {},
        { params: { dry_run: true }, headers: authHeader() });
      setDue(d);
    } catch { setDue(null); } finally { setPreviewing(false); }
  };

  return (
    <section className="mt-10" data-testid="onboarding-progress">
      <h2 className="text-base font-semibold tracking-tight text-slate-900 inline-flex items-center gap-2">
        <Route size={15} className="text-teal-600" /> Workshop setup progress
      </h2>
      <p className="text-xs text-slate-500 mt-0.5">
        {data
          ? `${data.count} ${data.count === 1 ? 'workshop' : 'workshops'}${data.stalled ? `, ${data.stalled} stalled before their first document` : ''}.`
          : 'Loading…'}{' '}
        Read from their live data, not from anything they ticked.
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          onClick={preview}
          disabled={previewing}
          className="inline-flex items-center gap-1.5 text-xs font-semibold border border-slate-300 hover:border-slate-400 text-slate-700 rounded-lg px-3 py-1.5"
          data-testid="welcome-preview-btn"
        >
          {previewing ? <Loader2 className="animate-spin" size={12} /> : <Mail size={12} />}
          Preview today&rsquo;s welcome emails
        </button>
        {due && (
          <span className="text-xs text-slate-500" data-testid="welcome-preview-result">
            {due.due === 0
              ? 'Nobody is due one today.'
              : `${due.due} due: ${due.results.map((r) => `${r.workshop || r.tenant_id} (day ${r.stage}, ${r.next_step})`).join(', ')}`}
          </span>
        )}
      </div>

      {loading ? (
        <div className="p-8 grid place-items-center"><Loader2 className="animate-spin text-slate-300" size={18} /></div>
      ) : (
        <div className="mt-3 bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
          {(data?.workshops || []).map((w) => (
            <div key={w.tenant_id} className="px-4 py-3" data-testid={`onboarding-row-${w.slug}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-slate-900">
                  {w.workshop_name || w.slug}
                </span>
                {w.stalled && (
                  <span
                    className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-1.5 py-0.5"
                    data-testid={`onboarding-stalled-${w.slug}`}
                  >
                    <PhoneCall size={9} /> Worth a call
                  </span>
                )}
                <span className="text-[10px] text-slate-400 ml-auto tabular-nums">
                  {w.done}/{w.total}
                </span>
              </div>
              <div className="h-1 mt-2 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full ${w.complete ? 'bg-emerald-500' : 'bg-teal-500'}`}
                  style={{ width: `${Math.round((w.done / w.total) * 100)}%` }}
                />
              </div>
              {!w.complete && (
                <p className="text-[11px] text-slate-500 mt-1.5">
                  Still to do: {w.pending.join(' · ')}
                </p>
              )}
            </div>
          ))}
          {!data?.count && (
            <p className="px-4 py-6 text-sm text-slate-500 text-center" data-testid="onboarding-progress-empty">
              No workshops yet besides your own.
            </p>
          )}
        </div>
      )}
    </section>
  );
};

export default OnboardingProgress;
